/**
 * Shared surface constants used by page chrome.
 *
 * Centralised so PageHero, SessionHero, GroupHeader, EntityHeader, and the
 * EducationSidebar all reference the same visual vocabulary.
 *
 * Two rules govern every value here:
 *
 *  1. **Light is the base, dark is the override.** The unprefixed classes
 *     describe the light appearance; `dark:` carries the night one. Author it
 *     the other way round and every surface ends up dark-first.
 *
 *  2. **Warm neutrals, cool accents.** Surfaces use `stone-*` (paper), while
 *     `primary` (blue) and `secondary` (amber) stay saturated. That contrast
 *     is what keeps a page of cards calm while actions stay obvious.
 *
 * Elevation is carried by a hairline border plus `shadow-sm` — not
 * `shadow-lg`. Heavy shadows on every card were the main reason the old UI
 * read as dense and dashboard-like.
 *
 * The `slate*` names are historical: the values are now stone/paper. The
 * names are kept so the ~36 existing call sites keep working; new code
 * should still use these same exports rather than hand-rolling classes.
 */
export const slateCardClass =
  "rounded-2xl border border-stone-200/80 bg-white shadow-sm dark:border-stone-800 dark:bg-stone-900";

/** Nested sub-surface: stat rows, grouped lists, side panels. */
export const slateSurfaceClass =
  "rounded-2xl border border-stone-200/70 bg-stone-50 p-3 dark:border-stone-800 dark:bg-stone-800/40";

/** Small label / count chip. */
export const metaChipClass =
  "inline-flex items-center gap-1.5 rounded-full bg-stone-100 px-2.5 py-1 text-xs font-medium text-stone-600 dark:bg-stone-800 dark:text-stone-400";

/** Square icon button (9x9). */
export const actionPillClass =
  "inline-flex items-center justify-center h-9 w-9 rounded-lg border border-stone-200 bg-white text-stone-500 transition-colors hover:bg-stone-50 hover:text-stone-800 dark:border-stone-800 dark:bg-stone-900 dark:text-stone-400 dark:hover:bg-stone-800 dark:hover:text-stone-200";

/**
 * The one primary action on a surface. Full-width by default because calm
 * focus allows exactly one of these per screen — it should read as *the*
 * next step, not one option among several.
 *
 * Flat `bg-primary-strong`, not a gradient. The blue→amber ramp put white on
 * the amber end at 2.4:1 — the app's most-pressed control was the least
 * readable thing on the page. 50% blue holds white at 5.05:1, and one accent
 * per screen is the whole point of "one action".
 */
export const primaryCtaClass =
  "h-10 w-full rounded-lg bg-primary-strong text-sm font-semibold text-primary-foreground shadow-sm transition-all hover:bg-primary-strong/90 active:scale-[0.98]";

/**
 * The one primary action inside a page hero (`PageHero.actions`, or a feature
 * hero with the same slots). Flat `bg-primary` — deliberately NOT a gradient.
 *
 * Hero CTAs used to be `bg-gradient-to-r from-primary to-secondary`. Two
 * problems with that on the element a page is asking you to press: the amber
 * end of the ramp pulled focus off the serif title it sits under, and a
 * two-colour fill made every hero read like a landing page rather than the
 * top of a study desk. Calm focus keeps the accent saturated but single —
 * warm stone surfaces, one blue action, one thing to do next.
 *
 * Sized and shaped to sit in the same row as the `variant="outline"` siblings
 * that share it (`rounded-full text-xs px-4 h-9`), so the pair reads as two
 * deliberate weights rather than two competing styles.
 */
export const heroCtaClass =
  "h-9 gap-1.5 rounded-full bg-primary-strong px-4 text-xs font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary-strong/90 active:scale-[0.98]";

/** Standard divider between sections inside a card. */
export const hairlineClass = "border-stone-200/70 dark:border-stone-800";

/**
 * The shell's panel seam — the right edge of the global nav rail, the right edge
 * of the contextual topics/conversations panel, and the border of the toggle
 * button that rides that seam. One token for all three so the button never
 * reads as a lighter line than the panel edge it straddles (it did in dark
 * mode, where the button stayed `stone-200` while both rails went `stone-800`).
 */
export const sidebarDividerClass = "border-stone-200 dark:border-stone-800";

/** Consistent keyboard focus treatment for interactive surfaces. */
export const focusRingClass =
  "outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background";

/**
 * Index-card surface for the vocabulary collection creator's signature
 * "lexicon deck" language. A warm paper tint against the cool slate
 * workspace so assembled words read as physical cards, not SaaS rows.
 * Deliberately NOT a full-page cream — only card surfaces use it.
 */
export const paperCardClass =
  "rounded-xl border border-stone-300/60 bg-stone-50 shadow-sm dark:border-stone-700/60 dark:bg-stone-900/60";

/**
 * Amber highlighter accent: a 2px underline beneath a word headword,
 * evoking highlighting a term you're learning. Used on the active /
 * freshly-added index card. The slight skew + low opacity mimic a
 * marker dragged by hand rather than a crisp rule.
 */
export const highlighterUnderlineClass =
  "bg-gradient-to-r from-amber-400/70 via-amber-400/50 to-transparent [clip-path:polygon(2%_0,98%_0,96%_100%,0_100%)]";

/* ────────────────────────────────────────────────────────────
   Aurora Chat — the AI topic-creation chat surface.

   The feed is treated as paper on a desk: the conversation sits on
   white cards with a hairline border and one soft shadow, the aurora
   wash sits *behind* it at low opacity, and the only saturated
   colour is the single blue action (send) plus the amber end of the
   accent ramp used sparingly on rails.

   Every in-chat component references these tokens instead of
   inventing its own palette.
   ──────────────────────────────────────────────────────────── */

/** Glass header bar. Pair with a gradient hairline underline. */
export const glassHeaderClass =
  "border-b border-stone-200/70 bg-background/70 backdrop-blur-xl supports-[backdrop-filter]:bg-background/60 dark:border-stone-800/60";

/**
 * Paper surface for anything the assistant says or produces: the turn
 * card, the tool cards (topic draft / web search / generation), and the
 * standalone skeletons. White + hairline + `shadow-sm` rather than the
 * old flat `stone-100` fill, which read as muddy grey once the text sat
 * on top of it.
 */
export const chatCardFlatClass =
  "relative overflow-hidden rounded-2xl border border-stone-200/80 bg-white shadow-sm dark:border-stone-800 dark:bg-stone-900";

/**
 * User message bubble — the learner's turn, in the app's blue.
 *
 * Went through three states worth recording:
 *  - solid `bg-primary` (the original): correct in spirit but blue-500 is
 *    only ~3.7:1 against white text, under AA for 15px body copy.
 *  - ink (`stone-900`): maximum contrast, but a black slab on a warm
 *    paper surface reads as a hole punched in the page.
 *  - a pale blue wash: the two sides of the conversation became nearly
 *    identical, so "who said this" stopped being readable at a glance.
 *
 * So: light mode takes one step deeper than `--primary` — the `primary-strong`
 * token at `217 91% 50%` (blue-600), the same hue at 5.2:1 with white — which
 * is loud enough to own its half of the conversation without shouting. Dark
 * mode uses a translucent primary instead: the same hue read as "raised", at a
 * lightness that doesn't glare against a dark page.
 */
export const chatBubbleUserClass =
  "rounded-2xl rounded-br-md bg-primary-strong px-4 py-2.5 text-[0.9375rem] leading-relaxed text-primary-foreground shadow-sm shadow-blue-900/10 dark:bg-primary/25 dark:text-stone-50 dark:ring-1 dark:ring-inset dark:ring-primary/40";

/**
 * The composer dock: a lifted card floating over the feed, with a
 * gradient hairline along its top edge so the "paper" it sits on reads
 * as a separate surface. Focus lifts it a little more.
 */
export const chatDockClass =
  "relative flex flex-col overflow-hidden rounded-[26px] border border-stone-200/80 bg-white/90 backdrop-blur-xl shadow-[0_18px_40px_-24px_rgba(28,25,23,0.35)] transition-all duration-300 focus-within:border-primary/40 focus-within:shadow-[0_22px_48px_-24px_rgba(28,25,23,0.4)] focus-within:ring-2 focus-within:ring-primary/15 dark:border-stone-800 dark:bg-stone-900/85";

/** The blue→transparent hairline riding the top edge of the dock. */
export const chatDockRailClass =
  "pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent";

/** Wrapper for the low-opacity aurora glow that sits behind the feed. */
export const auroraBackdropClass =
  "pointer-events-none absolute inset-0 -z-10 overflow-hidden";

/* ────────────────────────────────────────────────────────────
   Sidebar shell — the shared navigation rail vocabulary.

   Both the practice (My Topics) and AI (My Topics AI) sidebars
   compose these so a section header, a list row, the "New …"
   action, and the active state read identically regardless of
   feature. The shell background lives once on the AppShell panel
   wrapper; sidebars themselves stay transparent.
   ──────────────────────────────────────────────────────────── */

/** Collapsible section header — chevron + label, muted until hover. */
export const sidebarSectionHeaderClass =
  "flex w-full items-center gap-1.5 px-2 py-2.5 text-sm font-semibold text-foreground/90 hover:text-primary outline-none transition-colors";

/** List row — rounded hit area; pair with sidebarRowActiveClass when current. */
export const sidebarRowClass =
  "group flex items-center gap-2.5 px-2 py-1.5 rounded-md cursor-pointer transition-colors hover:bg-primary/10 hover:text-primary";

/** Active list row tint — primary wash + primary text. */
export const sidebarRowActiveClass = "bg-primary/10 text-primary";

/** Gradient "New …" primary action button. */
export const sidebarNewButtonClass =
  "w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-md bg-primary-strong text-primary-foreground hover:bg-primary-strong/90 transition-colors shadow-sm text-sm font-medium";

/* ────────────────────────────────────────────────────────────
   Menus & segmented controls — the profile dropdown and the
   streak modal share one vocabulary for "a row you can press"
   and "a small set of options you pick between".

   The dropdown is a paper card that floats over the app, so it
   wears the same hairline + soft shadow as every other surface.
   Rows hover on stone rather than the cool `--muted` the shared
   `DropdownMenuItem` defaults to — that mismatch was why the menu
   read as a different app from the rail it hangs off.
   ──────────────────────────────────────────────────────────── */

/** The floating panel: `DropdownMenuContent` surface, as a paper card. */
export const menuPanelClass =
  "rounded-2xl border border-stone-200/80 bg-white p-1.5 shadow-lg shadow-stone-900/[0.08] dark:border-stone-800 dark:bg-stone-900";

/**
 * A pressable row inside the panel — icon, label, optional trailing meta.
 *
 * Overrides the `hover:`/`focus:` fills on the shared `DropdownMenuItem` so a
 * menu row and a settings row look like the same product. Composed with
 * `menuRowIconClass` at the leading edge.
 */
export const menuRowClass =
  "flex w-full cursor-pointer select-none items-center gap-2.5 rounded-lg px-2 py-2 text-sm font-medium text-stone-600 outline-none transition-colors data-[disabled]:pointer-events-none data-[disabled]:opacity-50 hover:bg-stone-100 hover:text-stone-900 focus:bg-stone-100 focus:text-stone-900 dark:text-stone-400 dark:hover:bg-stone-800 dark:hover:text-stone-100 dark:focus:bg-stone-800 dark:focus:text-stone-100";

/** Icon well at the leading edge of a `menuRowClass` row. */
export const menuRowIconClass =
  "flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-stone-100 text-stone-500 transition-colors dark:bg-stone-800 dark:text-stone-400";

/** Hairline between groups inside a menu. Inset to the panel's own padding. */
export const menuSeparatorClass = "-mx-1.5 my-1.5 h-px bg-stone-200/70 dark:bg-stone-800";

/**
 * Segmented-control track — pick one of two-to-four options. Theme switcher
 * in the profile menu, and the streak modal's panel switcher.
 */
export const segmentedTrackClass =
  "inline-flex items-center gap-0.5 rounded-xl border border-stone-200/80 bg-stone-100/70 p-0.5 dark:border-stone-800 dark:bg-stone-800/60";

/**
 * One segment. The *active* segment adds its own white pill — `segmentedTrackClass`
 * is the recessive stone, so the selected option is a paper chip lifted off it
 * rather than a saturated block.
 *
 * `min-w-0` is load-bearing, not defensive: `flex-1` is `flex: 1 1 0%`, and
 * without it a segment refuses to shrink below its content width. Four
 * icon-plus-label tabs in a 390px dialog then pushed the last one 24px past
 * the viewport edge — the track had `w-full` and nowhere to go.
 */
export const segmentedItemClass =
  "inline-flex min-w-0 flex-1 items-center justify-center gap-1.5 rounded-[10px] px-2.5 py-1.5 text-xs font-medium text-stone-500 outline-none transition-colors hover:text-stone-800 focus-visible:ring-2 focus-visible:ring-ring dark:text-stone-400 dark:hover:text-stone-100";

/** The active segment's lifted pill, composed onto `segmentedItemClass`. */
export const segmentedItemActiveClass =
  "bg-white text-stone-900 shadow-sm dark:bg-stone-700 dark:text-stone-50";

/**
 * The same segmented control, expressed for Radix `Tabs` (shadcn `TabsList` /
 * `TabsTrigger`). Panel-switching surfaces keep Radix for its keyboard and
 * ARIA behaviour, but they must not invent a fourth shape: these two classes
 * are the tab version of `SegmentedControl`, so a tab strip and a filter row
 * read as the same object.
 *
 * The selected pill is ink rather than the primary blue. Selection is not the
 * page's action, and five blue pills competing with the one blue CTA is how a
 * page stops having a single next step.
 */
export const segmentedTabsListClass =
  "inline-flex h-auto max-w-full items-center justify-start gap-0.5 overflow-x-auto rounded-xl border border-stone-200/80 bg-stone-100/70 p-1 scrollbar-hide dark:border-stone-800 dark:bg-stone-800/60";

export const segmentedTabTriggerClass =
  "inline-flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-[10px] px-3.5 text-sm font-medium text-stone-500 outline-none transition-colors hover:text-stone-800 focus-visible:ring-2 focus-visible:ring-ring dark:text-stone-400 dark:hover:text-stone-100 data-[state=active]:bg-foreground data-[state=active]:text-background data-[state=active]:shadow-sm dark:data-[state=active]:bg-stone-100 dark:data-[state=active]:text-stone-900";

/* ────────────────────────────────────────────────────────────
   Identity & streak — the surfaces behind an avatar or a flame.

   The profile menu, the profile dialog, and the streak dialog
   all open with "who you are" or "how long you have kept
   going". A hairline on white answers neither: it reads as a
   settings list. These give those two moments a warm wash and
   one saturated accent, which is what makes the same three
   components look designed rather than generated.

   The wash is the same one `PageHero` uses — stone → paper →
   a trace of amber. It is deliberately the *opposite* end of
   the ramp from `primaryCtaClass`: a background to sit under
   text, not a fill behind a button.
   ──────────────────────────────────────────────────────────── */

/** Warm wash for an identity or streak header surface. */
export const identityWashClass =
  "bg-gradient-to-br from-amber-50/70 via-background to-primary/5 dark:from-amber-950/25 dark:via-background dark:to-primary/10";

/**
 * The avatar's ring. A gradient ring (rather than the flat `primary/20` the
 * plain `UserAvatar` draws) so an initials-only avatar reads as a deliberate
 * mark instead of a missing image.
 */
export const identityRingClass =
  "ring-2 ring-offset-2 ring-offset-background ring-gradient-to-br ring-from-primary/60 ring-to-secondary/60";

/**
 * Streak accent tile — the flame's square. Amber wash when a streak is live,
 * stone when it is not, so zero is visibly "not yet" without a word of copy.
 */
export const streakTileClass =
  "bg-gradient-to-br from-amber-100 to-orange-200 text-orange-600 shadow-sm dark:from-amber-900/60 dark:to-orange-950/60 dark:text-amber-300";

/** Streak accent tile with no streak running. */
export const streakTileIdleClass =
  "bg-stone-100 text-stone-400 dark:bg-stone-800 dark:text-stone-500";

/* ────────────────────────────────────────────────────────────
   Sidebar footer — the rail's bottom strip.
   ──────────────────────────────────────────────────────────── */

/**
 * Highlighted tile for the global rail's footer controls — the profile trigger,
 * the notification bell, and the collapse toggle all share one surface so the
 * bottom of the rail reads as a single strip of paper cards instead of loose
 * icons floating on the panel.
 */
export const sidebarFooterTileClass =
  "rounded-xl border border-stone-200/80 bg-white shadow-sm transition-colors hover:border-primary/40 hover:bg-stone-50 dark:border-stone-800 dark:bg-stone-900 dark:hover:border-primary/40 dark:hover:bg-stone-800/60";

/* ────────────────────────────────────────────────────────────
   Practice modes — the shared study-card chrome for the four
   vocabulary practice modes (Flashcard / Write / Listening /
   Scramble). Every mode's main surface composes these so the
   padding, border, and shadow read identically across modes.
   ──────────────────────────────────────────────────────────── */

/* ────────────────────────────────────────────────────────────
   Immersive Focus — the practice stage.

   The one deliberate calm-focus exception: practice is a single
   job done in a quiet dark room. The stage reads as a spotlighted
   surface — generous radius, a hairline border, one soft top glow
   — and every control on it stays low and functional. Chrome is
   sans furniture; the word itself is the only Fraunces voice.

   Light is still the base and dark the override: the room simply
   goes deeper at night. New practice code should compose these
   instead of hand-rolling surfaces.
   ──────────────────────────────────────────────────────────── */

/**
 * The stage card surface. Generous radius, hairline border, and a
 * wide soft shadow so the card reads as lifted off the desk.
 * Composed into `<Card>` alongside `modeCardClass`.
 */
export const focusStageClass =
  "rounded-[26px] border-stone-200/70 bg-white shadow-[0_28px_64px_-40px_rgba(28,25,23,0.38)] dark:border-white/[0.08] dark:bg-stone-900/60 dark:shadow-[0_30px_80px_-48px_rgba(0,0,0,0.9)]";

/** Nested sub-surface inside the stage: cloze box, tile pool, prompts. */
export const focusSurfaceClass =
  "rounded-2xl border border-stone-200/70 bg-stone-50/80 dark:border-white/[0.06] dark:bg-white/[0.03]";

/** Quiet pill control — secondary actions (Hear it, Replay, Normal/Slow). */
export const focusControlClass =
  "inline-flex h-9 items-center justify-center gap-1.5 rounded-full border border-stone-200 bg-white px-3.5 text-xs font-medium text-stone-600 transition-colors hover:border-primary/40 hover:text-primary disabled:pointer-events-none disabled:opacity-50 dark:border-white/10 dark:bg-white/[0.04] dark:text-stone-300 dark:hover:border-primary/40 dark:hover:text-primary";

/** The one filled control on the stage (Check / Stop & check / Start). */
export const focusControlPrimaryClass =
  "inline-flex h-9 items-center justify-center gap-1.5 rounded-full bg-primary px-4 text-xs font-semibold text-primary-foreground shadow-sm transition-all hover:brightness-105 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50";

/** Answer letter tile — Write/Listening slots and the Scramble pool. */
export const focusTileClass =
  "inline-flex items-center justify-center rounded-xl border-stone-200 bg-white text-lg font-semibold tabular-nums text-stone-800 shadow-sm transition-colors dark:border-white/10 dark:bg-white/[0.04] dark:text-stone-100";

/**
 * Unified practice card chrome: hairline border + soft shadow.
 * Composable into any Card usage, including the flashcard's
 * absolutely-positioned 3D flip faces. Pair with `focusStageClass`
 * for the full stage treatment.
 */
export const modeCardClass =
  "border-stone-200/70 dark:border-white/[0.08] shadow-sm dark:shadow-none";
